import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Webcam from 'react-webcam';
import styled from 'styled-components';
import { isMobileOnly } from 'react-device-detect';
import Paragraph from '../Paragraph/Paragraph';
import { webcamCaptureHelper } from '../../util/helpers/webcamCaptureHelper';
import MobilePhoneIcon from '../Icons/MobilePhoneIcon';
import WebcamIcon from '../Icons/WebcamIcon';
import ButtonPrimary from '../Buttons/Button';
import CameraAccessModal from '../Modals/CameraAccessModal/CameraAccessModal';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: ${variables.borderRadius.borderRadiusMd};
  border: 1px dashed ${themeColors.colorBorderPrimaryDark};
  background: ${themeColors.colorBackgroundDropzone};
  text-align: center;
  padding: 0.75rem;
  outline: none;
  height: 184px;
  max-width: 274px;
  width: 100%;
  margin-bottom: 1rem;

  > video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  button {
    max-width: 120px;
    width: 100%;
  }
`;

const videoConstraints = {
  width: 3266,
  height: 2449,
};

const WebcamCapture = ({ onCapture, frontSide, backSide, isPassport }) => {
  const webcamRef = React.useRef(null);
  const [allowCapture, setAllowCapture] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [isCameraEnabledModal, setIsCameraEnabledModal] = useState(false);

  const checkCameraPermissions = () => {
    navigator.getMedia =
      navigator.getUserMedia || // use the proper vendor prefix
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;

    navigator.getMedia(
      { video: true },
      () => {
        setAllowCapture(true);
      },
      () => {
        setIsCameraEnabledModal(true);
      },
    );
  };

  const capture = () => {
    const screenshot = webcamRef.current.getScreenshot();
    setImgSrc(screenshot);

    if (screenshot) {
      webcamCaptureHelper(
        screenshot,
        frontSide ? 'frontCapture.png' : 'backCapture.png',
        onCapture,
      );
      setAllowCapture(false);
    }
  };

  return (
    <>
      {!allowCapture && (
        <Wrap>
          <Container>
            {!imgSrc && (
              <>
                {isMobileOnly ? <MobilePhoneIcon /> : <WebcamIcon />}
                {frontSide && (
                  <Paragraph mt={12} variant="large" fontWeight={600}>
                    Capture Front
                  </Paragraph>
                )}
                {isPassport && (
                  <Paragraph mt={12} variant="large" fontWeight={600}>
                    Capture ID
                  </Paragraph>
                )}
                {backSide && (
                  <Paragraph mt={12} variant="large" fontWeight={600}>
                    Capture Back
                  </Paragraph>
                )}
              </>
            )}
            {imgSrc && <img src={imgSrc} alt="Front" />}
          </Container>
          <ButtonPrimary
            onClick={() => checkCameraPermissions()}
            title="Activate"
          />
        </Wrap>
      )}
      {allowCapture && (
        <Wrap>
          <Container>
            <Webcam
              audio={false}
              width={3266}
              height={2449}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              imageSmoothing
              screenshotQuality={1}
              forceScreenshotSourceSize
            />
          </Container>
          <ButtonPrimary onClick={capture} title="Capture" />
        </Wrap>
      )}
      <CameraAccessModal
        isOpen={isCameraEnabledModal}
        onClose={() => setIsCameraEnabledModal(false)}
      />
    </>
  );
};

WebcamCapture.propTypes = {
  onCapture: PropTypes.func,
  frontSide: PropTypes.bool,
  backSide: PropTypes.bool,
  isPassport: PropTypes.bool,
};

export default WebcamCapture;
